import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { getDeviceInfo } from '../utils/deviceInfo';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    IdentificationIcon,
    CheckIcon,
    XMarkIcon,
    ClockIcon,
    MapPinIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ChevronLeftIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL;

// Skeleton Loading Components
const ProfileHeaderSkeleton = () => (
    <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white animate-pulse">
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
            <div className="h-24 w-24 rounded-full border-4 border-white dark:border-gray-200 bg-blue-400 opacity-30"></div>
            <div className="space-y-3 flex-1">
                <div className="h-8 w-3/4 bg-blue-500 opacity-30 rounded"></div>
                <div className="h-5 w-1/2 bg-blue-500 opacity-30 rounded"></div>
                <div className="flex gap-2">
                    <div className="h-6 w-20 bg-blue-500 opacity-30 rounded-full"></div>
                    <div className="h-6 w-24 bg-blue-500 opacity-30 rounded-full"></div>
                </div>
            </div>
        </div>
    </div>
);

const ProfileDetailSkeleton = ({ icon: Icon }) => (
    <div className="flex items-start">
        <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
            <Icon className="h-5 w-5 opacity-30" />
        </div>
        <div className="ml-3 space-y-2 flex-1">
            <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-600 rounded opacity-30"></div>
            <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-600 rounded opacity-30"></div>
        </div>
    </div>
);

const LoginHistorySkeleton = () => (
    <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400 opacity-30">
                    <ClockIcon className="h-5 w-5" />
                </div>
                <div className="ml-3 space-y-2 flex-1">
                    <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-600 rounded opacity-30"></div>
                    <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-600 rounded opacity-30"></div>
                </div>
            </div>
        ))}
    </div>
);

const TableRowSkeleton = () => (
    <tr>
        {[...Array(3)].map((_, i) => (
            <td key={i} className="px-4 py-3 whitespace-nowrap">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded opacity-30 w-3/4"></div>
            </td>
        ))}
    </tr>
);

const MyProfile = () => {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [access_data, setAccess_data] = useState(null);
    const [showAllLogins, setShowAllLogins] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username) {
            fetchProfileData();
        }
    }, [access_data?.username]);

    const fetchProfileData = async () => {
        setLoading(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {}
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}my-profile.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                if (result.login_info && result.login_info.length > 0) {
                    result.login_info.sort((a, b) => parseInt(b.time) - parseInt(a.time));
                }
                setProfileData(result);
            } else {
                toast.error(result.message || "Failed to fetch profile data.");
            }
        } catch (error) {
            console.error("Fetch profile error:", error);
            toast.error("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const getDesignationText = (designationCode) => {
        const designations = {
            '1': 'Admin',
            '2': 'Super Admin',
            '3': 'Distributor',
            '4': 'Retailer'
        };
        return designations[designationCode] || designationCode;
    };

    const getStatusBadge = (status) => {
        return status === '1' ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <CheckIcon className="h-3 w-3 mr-1" />
                Active
            </span>
        ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                <XMarkIcon className="h-3 w-3 mr-1" />
                Inactive
            </span>
        );
    };

    const formatDateTime = (timestamp, dateStr) => {
        if (!timestamp && !dateStr) return 'N/A';

        try {
            if (timestamp) {
                const date = new Date(parseInt(timestamp) * 1000);
                return date.toLocaleString();
            }
            if (dateStr) {
                const date = new Date(dateStr);
                return date.toLocaleString();
            }
        } catch (e) {
            console.error("Error formatting date:", e);
        }
        return 'N/A';
    };

    const getLatestLogin = () => {
        if (!profileData?.login_info || profileData.login_info.length === 0) return null;
        return profileData.login_info[0];
    };

    const toggleLoginHistory = () => {
        setShowAllLogins(!showAllLogins);
    };

    return (

        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                            >
                                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                Back
                            </button>
                            <h2 className="text-xl font-semibold">
                                My Profile
                            </h2>
                            <div></div>
                        </div>

                        {loading ? (
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                                {/* Profile Header Skeleton */}
                                <ProfileHeaderSkeleton />

                                {/* Profile Details Skeleton */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                                    {/* Personal Information Skeleton */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
                                            Personal Information
                                        </h3>
                                        <div className="space-y-3">
                                            <ProfileDetailSkeleton icon={UserIcon} />
                                            <ProfileDetailSkeleton icon={IdentificationIcon} />
                                            <ProfileDetailSkeleton icon={EnvelopeIcon} />
                                            <ProfileDetailSkeleton icon={PhoneIcon} />
                                        </div>
                                    </div>

                                    {/* Login Information Skeleton */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
                                            Login Information
                                        </h3>
                                        <LoginHistorySkeleton />
                                    </div>
                                </div>

                                {/* Login History Section Skeleton */}
                                <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                            Login History
                                        </h3>
                                        <div className="h-8 w-24 bg-gray-200 dark:bg-gray-600 rounded opacity-30"></div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                            <thead className="bg-gray-100 dark:bg-gray-600">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date & Time</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Location</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                                {[...Array(3)].map((_, i) => (
                                                    <TableRowSkeleton key={i} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        ) : profileData ? (
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                                {/* Profile Header */}
                                <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
                                    <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
                                        <div className="relative">
                                            {profileData.user.avatar_path ? (
                                                <img
                                                    src={profileData.user.avatar_path}
                                                    alt="Profile"
                                                    className="h-24 w-24 rounded-full border-4 border-white dark:border-gray-200 object-cover"
                                                />
                                            ) : (
                                                <div className="h-24 w-24 rounded-full border-4 border-white dark:border-gray-200 bg-blue-400 flex items-center justify-center">
                                                    <UserIcon className="h-12 w-12 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold">{profileData.user.name}</h2>
                                            <p className="text-blue-100">{profileData.user.username}</p>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {getStatusBadge(profileData.user.status)}
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                    {getDesignationText(profileData.user.designation)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Profile Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                                    {/* Personal Information */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
                                            Personal Information
                                        </h3>
                                        <div className="space-y-3">
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
                                                    <UserIcon className="h-5 w-5" />
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Full Name</p>
                                                    <p className="text-sm text-gray-900 dark:text-white">{profileData.user.name}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
                                                    <IdentificationIcon className="h-5 w-5" />
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Username</p>
                                                    <p className="text-sm text-gray-900 dark:text-white">{profileData.user.username}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
                                                    <EnvelopeIcon className="h-5 w-5" />
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                                                    <p className="text-sm text-gray-900 dark:text-white">{profileData.user.email || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
                                                    <PhoneIcon className="h-5 w-5" />
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</p>
                                                    <p className="text-sm text-gray-900 dark:text-white">{profileData.user.phone || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Login Information */}
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b pb-2">
                                            Login Information
                                        </h3>
                                        {getLatestLogin() && (
                                            <div className="space-y-3">
                                                <div className="flex items-start">
                                                    <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
                                                        <ClockIcon className="h-5 w-5" />
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</p>
                                                        <p className="text-sm text-gray-900 dark:text-white">
                                                            {formatDateTime(getLatestLogin().time, getLatestLogin().date)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start">
                                                    <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
                                                        <MapPinIcon className="h-5 w-5" />
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</p>
                                                        <p className="text-sm text-gray-900 dark:text-white">
                                                            {getLatestLogin().latitude && getLatestLogin().longitude ? (
                                                                <a
                                                                    href={`https://www.google.com/maps?q=${getLatestLogin().latitude},${getLatestLogin().longitude}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 hover:underline dark:text-blue-400"
                                                                >
                                                                    View on Map
                                                                </a>
                                                            ) : 'N/A'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-start">
                                                    <div className="flex-shrink-0 h-5 w-5 text-gray-500 dark:text-gray-400">
                                                        <CheckIcon className="h-5 w-5" />
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Login Status</p>
                                                        <p className="text-sm text-gray-900 dark:text-white">
                                                            {getStatusBadge(getLatestLogin().status)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Login History Section */}
                                {profileData.login_info && profileData.login_info.length > 0 && (
                                    <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
                                        <div className="flex justify-between items-center mb-3">
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                                Login History
                                            </h3>
                                            <button
                                                onClick={toggleLoginHistory}
                                                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                                            >
                                                {showAllLogins ? (
                                                    <>
                                                        <ChevronUpIcon className="h-4 w-4 mr-1" />
                                                        Show Less
                                                    </>
                                                ) : (
                                                    <>
                                                        <ChevronDownIcon className="h-4 w-4 mr-1" />
                                                        Show All ({profileData.login_info.length})
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                                <thead className="bg-gray-100 dark:bg-gray-600">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date & Time</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Location</th>
                                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                                    {profileData.login_info.slice(0, showAllLogins ? profileData.login_info.length : 3).map((login, index) => (
                                                        <tr key={index}>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {formatDateTime(login.time, login.date)}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {login.latitude && login.longitude ? (
                                                                    <a
                                                                        href={`https://www.google.com/maps?q=${login.latitude},${login.longitude}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-blue-600 hover:underline dark:text-blue-400"
                                                                    >
                                                                        View on Map
                                                                    </a>
                                                                ) : 'N/A'}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                                {getStatusBadge(login.status)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center">
                                <p className="text-gray-600 dark:text-gray-400">Failed to load profile data. Please try again.</p>
                                <button
                                    onClick={fetchProfileData}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Retry
                                </button>
                            </div>
                        )}
                    </div>
                </main>
            </div>
            <ToastContainer />
        </div>
    );
};

export default MyProfile;